// "Devices registered" section of the order statistics page.
// A device is registered when it first gets an owner in SeeYou Cloud.
// Only Oudie N, Omni and Nimo report ownership.
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle, ArrowRightLeft, ChevronRight, Clock, PackageX, Percent, RefreshCw, UserCheck,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import type { RegistrationBucket } from './RegistrationCharts';

const RegistrationCharts = lazy(() => import('./RegistrationCharts'));

interface ProductRow {
  product_name: string;
  product_id: number | string | null;
  registered: number;
  sold: number;
  sold_checked: number;
  sold_registered: number;
  registration_rate: number | null;
  median_days: number | null;
}

interface SaleCounts {
  sold: number;
  checked: number;
  registered: number;
  not_registered: number;
  not_checked: number;
  registration_rate: number | null;
  median_days: number | null;
}

interface CustomerProductRow extends SaleCounts {
  product_name: string;
}

interface CustomerRow {
  customer_key: string; // "woo:<account id>", or the name for orders without an account
  customer: string;
  woo_customer_id: number | null;
  country: string | null;
  products: CustomerProductRow[];
  sold: number;
  checked: number;
  registered: number;
  not_registered: number;
  not_checked: number;
  registration_rate: number | null;
  median_days: number | null;
}

interface SyncRun {
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'completed' | 'partial' | 'failed' | 'interrupted';
  devices_total: number | null;
  devices_checked: number;
  failed_batches: number;
  last_error: string | null;
}

export interface RegistrationStatsData {
  granularity: 'day' | 'week' | 'month';
  summary: {
    registered: number;
    sold: number;
    sold_checked: number;
    sold_registered: number;
    registration_rate: number | null;
    median_days_to_register: number | null;
    transfers: number;
  };
  all_time: {
    eligible_devices: number;
    checked_devices: number;
    registered_devices: number;
    resold_devices: number;
    unregistered_after_90_days: number;
  };
  families: string[];
  delay_buckets: string[];
  timeline: RegistrationBucket[];
  by_product: ProductRow[];
  by_customer: CustomerRow[];
  last_sync: SyncRun | null;
}

interface RegistrationStatsProps {
  period: string;
  customStart: string;
  customEnd: string;
}

const CUSTOMER_ROWS_COLLAPSED = 15;

const formatRate = (rate: number | null) => (rate == null ? '–' : `${rate}%`);
const formatDays = (days: number | null) => (days == null ? '–' : `${days} d`);

const formatDateTime = (value: string | null) => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  value: React.ReactNode;
  note: React.ReactNode;
}> = ({ icon, iconClass, title, value, note }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${iconClass}`}>{icon}</div>
      <div className="min-w-0">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
      </div>
    </div>
    <p className="text-xs text-gray-500 mt-2">{note}</p>
  </div>
);

const th = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider';
const td = 'px-4 py-3 whitespace-nowrap text-sm';

// The count cells shared by a customer row and its product rows.
const SaleCountCells: React.FC<{ row: SaleCounts; showNotChecked: boolean }> = ({ row, showNotChecked }) => (
  <>
    <td className={`${td} text-right tabular-nums`}>{row.sold}</td>
    <td className={`${td} text-right tabular-nums`}>{row.registered}</td>
    <td className={`${td} text-right tabular-nums`}>{row.not_registered}</td>
    {showNotChecked && <td className={`${td} text-right tabular-nums text-gray-500`}>{row.not_checked}</td>}
    <td className={`${td} text-right tabular-nums`}>{formatRate(row.registration_rate)}</td>
    <td className={`${td} text-right tabular-nums`}>{formatDays(row.median_days)}</td>
  </>
);

// A customer row; a click opens its products as rows below, in the same columns.
const CustomerRows: React.FC<{
  row: CustomerRow;
  expanded: boolean;
  onToggle: () => void;
  showNotChecked: boolean;
}> = ({ row, expanded, onToggle, showNotChecked }) => (
  <>
    <tr onClick={onToggle} className={`cursor-pointer ${expanded ? 'bg-blue-50/40' : 'hover:bg-gray-50'}`}>
      <td className={`${td} font-medium text-gray-900`}>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="inline-flex items-center gap-1.5 text-left"
          title={row.woo_customer_id ? `WooCommerce customer #${row.woo_customer_id}` : 'No WooCommerce account (guest or old order)'}
        >
          <ChevronRight
            className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
          {row.customer}
        </button>
      </td>
      <td className={`${td} text-gray-500`}>{row.country || '–'}</td>
      <SaleCountCells row={row} showNotChecked={showNotChecked} />
    </tr>
    {expanded && row.products.map((product) => (
      <tr key={product.product_name} className="bg-gray-50 text-gray-700">
        <td className={`${td} pl-14`}>{product.product_name}</td>
        <td className={td} />
        <SaleCountCells row={product} showNotChecked={showNotChecked} />
      </tr>
    ))}
  </>
);

const SyncStatus: React.FC<{ run: SyncRun | null; isAdmin: boolean; syncing: boolean; onSync: () => void }> = ({
  run, isAdmin, syncing, onSync,
}) => {
  let text = 'Ownership has not been synced from SeeYou Cloud yet.';
  if (run) {
    if (run.status === 'running') {
      text = `Sync running since ${formatDateTime(run.started_at)}…`;
    } else {
      text = `Last sync ${formatDateTime(run.finished_at || run.started_at)}: `
        + `${run.devices_checked} of ${run.devices_total ?? '?'} devices checked`;
      if (run.status === 'partial') text += `, ${run.failed_batches} batches failed`;
      if (run.status === 'failed') text += ' (failed)';
      if (run.status === 'interrupted') text += ' (interrupted)';
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
      <span title={run?.last_error || undefined}>{text}</span>
      {isAdmin && (
        <button
          type="button"
          onClick={onSync}
          disabled={syncing || run?.status === 'running'}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${syncing || run?.status === 'running' ? 'animate-spin' : ''}`} />
          Sync now
        </button>
      )}
    </div>
  );
};

const RegistrationStats: React.FC<RegistrationStatsProps> = ({ period, customStart, customEnd }) => {
  const { user } = useAuth() as { user: { role?: string } | null };
  const isAdmin = user?.role === 'admin';

  const [stats, setStats] = useState<RegistrationStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(() => new Set());

  const toggleCustomer = (customerKey: string) => {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customerKey)) next.delete(customerKey);
      else next.add(customerKey);
      return next;
    });
  };

  const fetchStats = useCallback(async () => {
    try {
      setError('');
      let url = `/api/stats/registrations?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `/api/stats/registrations?start=${customStart}&end=${customEnd}`;
      }
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching registration stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch registration statistics');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  // While a sync runs, refresh every 10 seconds so the numbers fill in.
  const syncRunning = stats?.last_sync?.status === 'running';
  useEffect(() => {
    if (!syncRunning) return undefined;
    const timer = setInterval(fetchStats, 10000);
    return () => clearInterval(timer);
  }, [syncRunning, fetchStats]);

  const startSync = async () => {
    try {
      setSyncing(true);
      await axios.post('/api/stats/registrations/sync');
      // The run row appears within a moment; fetch then, and polling takes over.
      setTimeout(fetchStats, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start the ownership sync');
    } finally {
      setSyncing(false);
    }
  };

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Devices registered</h2>
        <p className="text-sm text-gray-500">
          A device is registered when it first gets an owner in SeeYou Cloud. Oudie N, Omni and Nimo only.
        </p>
      </div>
      {stats && <SyncStatus run={stats.last_sync} isAdmin={isAdmin} syncing={syncing} onSync={startSync} />}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 pt-4">
        {header}
        <div className="bg-white rounded-lg shadow h-40 animate-pulse" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-4 pt-4">
        {header}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error || 'No data'}</span>
        </div>
      </div>
    );
  }

  const { summary, all_time: allTime } = stats;
  const incomplete = allTime.checked_devices < allTime.eligible_devices;
  const customers = showAllCustomers ? stats.by_customer : stats.by_customer.slice(0, CUSTOMER_ROWS_COLLAPSED);
  const anyNotChecked = stats.by_customer.some((c) => c.not_checked > 0);

  return (
    <div className="space-y-6 pt-4">
      {header}

      {incomplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Ownership is known for {allTime.checked_devices} of {allTime.eligible_devices} devices.
            Devices that were not checked yet are left out of the registration rates.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard
          icon={<UserCheck className="w-5 h-5 text-white" />}
          iconClass="bg-blue-600"
          title="Devices registered"
          value={summary.registered}
          note="First owner in this period"
        />
        <StatCard
          icon={<Percent className="w-5 h-5 text-white" />}
          iconClass="bg-green-600"
          title="Registration rate"
          value={formatRate(summary.registration_rate)}
          note={`${summary.sold_registered} of ${summary.sold_checked} devices sold in this period are registered now`}
        />
        <StatCard
          icon={<Clock className="w-5 h-5 text-white" />}
          iconClass="bg-indigo-500"
          title="Median time to register"
          value={formatDays(summary.median_days_to_register)}
          note="From sale to first owner, for devices registered in this period"
        />
        <StatCard
          icon={<ArrowRightLeft className="w-5 h-5 text-white" />}
          iconClass="bg-purple-500"
          title="Owner transfers"
          value={summary.transfers}
          note={`Detected in this period. ${allTime.resold_devices} devices (all time) are owned by someone other than the first owner.`}
        />
        <StatCard
          icon={<PackageX className="w-5 h-5 text-white" />}
          iconClass="bg-orange-500"
          title="Not registered after 90 days"
          value={allTime.unregistered_after_90_days}
          note="Sold more than 90 days ago and still without an owner (all time)"
        />
      </div>

      <Suspense fallback={<div className="bg-white rounded-lg shadow h-96 animate-pulse" />}>
        <RegistrationCharts
          timeline={stats.timeline}
          families={stats.families}
          delayBuckets={stats.delay_buckets}
          granularity={stats.granularity}
        />
      </Suspense>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Registrations by product</h3>
          <p className="text-xs text-gray-500 mt-1">
            "Registered" counts devices registered in this period. The rate is for devices sold in this period.
          </p>
        </div>
        {stats.by_product.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No devices were sold or registered in this period</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${th} text-left`}>Product</th>
                  <th className={`${th} text-right`}>Registered</th>
                  <th className={`${th} text-right`}>Sold</th>
                  <th className={`${th} text-right`}>Sold and registered</th>
                  <th className={`${th} text-right`}>Rate</th>
                  <th className={`${th} text-right`}>Median time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.by_product.map((row) => (
                  <tr key={row.product_name} className="hover:bg-gray-50">
                    <td className={`${td} font-medium text-gray-900`}>{row.product_name}</td>
                    <td className={`${td} text-right tabular-nums`}>{row.registered}</td>
                    <td className={`${td} text-right tabular-nums`}>{row.sold}</td>
                    <td className={`${td} text-right tabular-nums`}>{row.sold_registered}</td>
                    <td className={`${td} text-right tabular-nums`}>{formatRate(row.registration_rate)}</td>
                    <td className={`${td} text-right tabular-nums`}>{formatDays(row.median_days)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="font-semibold text-gray-900">
                  <td className={td}>Total</td>
                  <td className={`${td} text-right tabular-nums`}>{summary.registered}</td>
                  <td className={`${td} text-right tabular-nums`}>{summary.sold}</td>
                  <td className={`${td} text-right tabular-nums`}>{summary.sold_registered}</td>
                  <td className={`${td} text-right tabular-nums`}>{formatRate(summary.registration_rate)}</td>
                  <td className={`${td} text-right tabular-nums`}>{formatDays(summary.median_days_to_register)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Registrations by sold-to customer</h3>
          <p className="text-xs text-gray-500 mt-1">
            Devices sold in this period, by the customer on the order, and whether they have an owner now.
            Click a customer to see its products.
          </p>
        </div>
        {stats.by_customer.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No devices were sold in this period</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`${th} text-left`}>Customer</th>
                    <th className={`${th} text-left`}>Country</th>
                    <th className={`${th} text-right`}>Sold</th>
                    <th className={`${th} text-right`}>Registered</th>
                    <th className={`${th} text-right`}>Not registered</th>
                    {anyNotChecked && <th className={`${th} text-right`}>Not checked</th>}
                    <th className={`${th} text-right`}>Rate</th>
                    <th className={`${th} text-right`}>Median time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((row) => (
                    <CustomerRows
                      key={row.customer_key}
                      row={row}
                      expanded={expandedCustomers.has(row.customer_key)}
                      onToggle={() => toggleCustomer(row.customer_key)}
                      showNotChecked={anyNotChecked}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {stats.by_customer.length > CUSTOMER_ROWS_COLLAPSED && (
              <div className="px-6 py-3 border-t border-gray-200 text-sm">
                <button
                  type="button"
                  onClick={() => setShowAllCustomers((v) => !v)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {showAllCustomers ? 'Show fewer' : `Show all ${stats.by_customer.length} customers`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RegistrationStats;
